import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, X } from "lucide-react";
import { fetchTours as fetchToursApi, getTour } from "@/lib/toursApi";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Checkbox } from "@/components/ui/checkbox";
import { DateInput } from "@/components/ui/date-input";
import { MoneyInput } from "@/components/ui/money-input";
import { shouldUseTourPriceOverride } from "@/lib/tourDurationPricing";
import { computeItemBaseAmount } from "@/lib/financialCalculations";
import { PriceWithAdjustment } from "@/components/proposals/PriceWithAdjustment";

interface ToursStepProps {
  data: any;
  onChange: (data: any) => void;
  readOnly?: boolean;
}

const ToursStep = ({ data, onChange, readOnly }: ToursStepProps) => {
  const isReadOnly = !!readOnly;
  const [tours, setTours] = useState<any[]>([]);
  const [proposalTours, setProposalTours] = useState<any[]>([]);
  const didInit = useRef(false);

  useEffect(() => {
    fetchTours();
  }, []);

  useEffect(() => {
    if (didInit.current) return;
    if (!Array.isArray(data.tours)) return; // wait for load
    const loadedTours = data.tours as any[];
    const sorted = [...loadedTours].sort((a, b) => {
      if (!a.tour_date) return -1;
      if (!b.tour_date) return 1;
      const dateCompare = a.tour_date.localeCompare(b.tour_date);
      if (dateCompare !== 0) return dateCompare;
      const timeA = a.time_from || "";
      const timeB = b.time_from || "";
      return timeA.localeCompare(timeB);
    });
    setProposalTours(sorted);
    didInit.current = true;
  }, []); // Only run once on mount

  const fetchTours = async () => {
    const payload = await fetchToursApi({ page: 1, size: 1000, sortField: "title", sortOrder: "asc" });
    setTours(payload.items || []);
  };

  const addTour = () => {
    if (isReadOnly) return;
    setProposalTours([{ tour_date: "", time_from: "", time_to: "", free_of_charge: false }, ...proposalTours]);
  };

  const removeTour = (index: number) => {
    if (isReadOnly) return;
    const newTours = proposalTours.filter((_, i) => i !== index);
    setProposalTours(newTours);
    onChange({ ...data, tours: newTours });
  };

  const removeInvalidPriceOverride = (tour: Record<string, unknown>) => {
    if (shouldUseTourPriceOverride(tour)) return tour;
    const { price_override, ...rest } = tour || {};
    return rest;
  };

  const updateTour = async (index: number, field: string, value: any) => {
    if (isReadOnly) return;
    const newTours = [...proposalTours];
    newTours[index] = removeInvalidPriceOverride({ ...newTours[index], [field]: value });
    if (field === "tour_id") {
      newTours[index].tours = undefined;
      newTours[index] = removeInvalidPriceOverride(newTours[index]);
      setProposalTours(newTours);
      onChange({ ...data, tours: newTours });

      if (value) {
        try {
          const selected = await getTour(Number(value));
          const updated = [...newTours];
          updated[index] = {
            ...updated[index],
            tours: {
              id: selected.id,
              tour_code: selected.tour_code,
              title: selected.title,
              description: selected.description,
              image_url: selected.image_url,
              duration_hours: selected.duration_hours,
              pricing_type: selected.pricing_type,
              price_op: selected.price_op,
              price_ag: selected.price_ag,
              price_cf: selected.price_cf,
            },
          };
          updated[index] = removeInvalidPriceOverride(updated[index]);
          setProposalTours(updated);
          onChange({ ...data, tours: updated });
        } catch {
          // Ignore lookup errors and keep manual selection flow.
        }
      }
      return;
    }
    setProposalTours(newTours);
    onChange({ ...data, tours: newTours });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Tours</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Add tour activities for the trip
          </p>
        </div>
        <Button type="button" size="sm" onClick={addTour} disabled={isReadOnly}>
          <Plus className="mr-2 h-4 w-4" />
          Add Tour
        </Button>
      </div>

      {proposalTours.length === 0 ? (
        <div
          className={`border-2 border-dashed rounded-lg p-12 text-center ${isReadOnly ? "" : "cursor-pointer transition-colors hover:bg-muted/30"}`}
          onClick={isReadOnly ? undefined : addTour}
          onKeyDown={isReadOnly ? undefined : (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              addTour();
            }
          }}
          role={isReadOnly ? undefined : "button"}
          tabIndex={isReadOnly ? -1 : 0}
          aria-label={isReadOnly ? undefined : "Add tour"}
        >
          <div className="flex flex-col items-center gap-3">
            <div className="rounded-full bg-muted p-3">
              <Plus className="h-6 w-6 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium">No tours added yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Click "Add Tour" to get started
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          {proposalTours.map((tour, index) => {
            const shouldShowPriceOverride = shouldUseTourPriceOverride(tour);
            
            return (
              <div key={index} className="grid gap-5 p-6 border rounded-lg bg-card relative shadow-sm hover:shadow-md transition-shadow">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute top-3 right-3 h-8 w-8 p-0"
                  onClick={() => removeTour(index)}
                  disabled={isReadOnly}
                >
                  <X className="h-4 w-4" />
                </Button>

                <div className="grid grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Tour</Label>
                    <SearchableSelect
                      items={tours.map((t) => ({
                        id: t.id,
                        code: t.tour_code,
                        title: t.title,
                        description: t.type || undefined,
                      }))}
                      value={tour.tour_id}
                      onValueChange={(value) => updateTour(index, "tour_id", parseInt(value.toString()))}
                      placeholder="Select tour"
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Tour Date</Label>
                    <DateInput
                      value={tour.tour_date}
                      onChange={(value) => updateTour(index, "tour_date", value)}
                      disabled={isReadOnly}
                    />
                  </div>
                </div>

                <div className="border-t pt-5">
                  <div className="grid grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">From Time</Label>
                      <Input
                        type="time"
                        value={tour.time_from || ""}
                        onChange={(e) => updateTour(index, "time_from", e.target.value)}
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">To Time</Label>
                      <Input
                        type="time"
                        value={tour.time_to || ""}
                        onChange={(e) => updateTour(index, "time_to", e.target.value)}
                        disabled={isReadOnly}
                      />
                    </div>
                  </div>
                </div>

                {shouldShowPriceOverride && (
                  <div className="border-t pt-5">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Override Price</Label>
                      <MoneyInput
                        value={tour.price_override ?? ""}
                        onChange={(rawValue) => {
                          if (!rawValue) {
                            updateTour(index, "price_override", undefined);
                            return;
                          }
                          const parsed = Number(rawValue);
                          updateTour(index, "price_override", Number.isFinite(parsed) ? parsed : undefined);
                        }}
                        className="w-[140px]"
                        disabled={isReadOnly}
                      />
                    </div>
                  </div>
                )}

                <div className="border-t pt-5 flex items-center justify-between gap-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      checked={tour.free_of_charge}
                      onCheckedChange={(checked) => updateTour(index, "free_of_charge", checked)}
                      disabled={isReadOnly}
                    />
                    <Label className="text-sm font-normal cursor-pointer">Free of Charge (FOC)</Label>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Item total</p>
                    <PriceWithAdjustment
                      baseAmount={tour.free_of_charge ? 0 : computeItemBaseAmount(tour, tour.tours, data)}
                      proposalData={data}
                      emptyLabel={tour.free_of_charge ? "Free" : !tour.tour_id ? "—" : undefined}
                      size="md"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ToursStep;
